/**
 * Tarayıcıda oluşan hataları sunucuya bildirir.
 *
 * Sunucu, ekranda bir şeyin kırıldığını kendiliğinden bilmez: istek başarıyla tamamlanmış,
 * hata sayfanın kendi içinde oluşmuştur. Bu dosya olmadan "bende çalışmıyor" diyen eczacının
 * ne gördüğünü ancak tarif ederek öğrenebiliriz.
 *
 * Bildirim gönderirken hiçbir zaman hata fırlatılmaz ve hiçbir şey beklenmez: hata takibinin
 * kendisi kullanıcının ekranında ikinci bir arıza kaynağı olmamalı.
 */

import { OPAS_DISPLAY_VERSION } from '@/generated/opasBuildMeta'
import { eskiPaketKaymasiMi, eskiPaketiBirKezYenile } from './eskiPaketKaymasi'
import { reactHatasiniCoz } from './reactHataAciklamalari'

const AYNI_HATA_SESSIZLIK_MS = 60_000
const OTURUM_BASINA_EN_FAZLA = 20

const gonderilenler = new Map<string, number>()
let gonderilenToplam = 0

type HataGirdisi = {
  tur?: string
  mesaj: string
  yiginIzi?: string
  /** React'in verdiği bileşen ağacı; yığın izi sıkıştırılmışken hatanın yerini bu ele verir. */
  bilesenYigini?: string
  /** Next.js'in ürettiği özet kod; sunucu günlüğündeki karşılığı bununla bulunur. */
  destekKodu?: string
}

/**
 * Ekran ölçüsüne bağlı hatalar («şu bilgisayarda oluyor, bende olmuyor») yalnızca yığın izine
 * bakarak çözülemez. Bu satır, kaydı okuyan kişiye ortamı tarif eder.
 */
function ortamOzeti(): string {
  const parcalar = [
    `pencere ${window.innerWidth}×${window.innerHeight}`,
    `ekran ${window.screen?.width ?? '?'}×${window.screen?.height ?? '?'}`,
    `dpr ${Math.round((window.devicePixelRatio || 1) * 100) / 100}`,
    navigator.language,
  ]
  return `Ortam: ${parcalar.join(' · ')}`
}

/**
 * Aynı hata çoğu zaman saniyede birkaç kez tekrarlar (yeniden çizim, döngüye giren bir efekt).
 * Hepsini göndermek hem ağ hem sunucu tarafında gereksiz yük; grup sayacı zaten sunucuda tutuluyor.
 */
function yakinZamandaGonderildiMi(anahtar: string): boolean {
  const simdi = Date.now()
  const oncekiZaman = gonderilenler.get(anahtar)

  if (oncekiZaman !== undefined && simdi - oncekiZaman < AYNI_HATA_SESSIZLIK_MS) return true

  gonderilenler.set(anahtar, simdi)
  return false
}

export function hataBildir(girdi: HataGirdisi): void {
  if (typeof window === 'undefined') return

  const hamMesaj = (girdi.mesaj || '').trim()
  if (!hamMesaj) return

  const tur = (girdi.tur || 'Error').trim()
  const kayma = eskiPaketKaymasiMi(tur, hamMesaj, girdi.yiginIzi)

  // Sonsuz döngüye giren bir sayfa tek başına kuyruğu doldurabilir; oturum başına bir tavan var.
  if (gonderilenToplam < OTURUM_BASINA_EN_FAZLA) {
    // Numaraya indirgenmiş React hatası kayda açık hâliyle düşsün; gruplama da anlamlı metin üzerinden yapılır.
    const reactHatasi = reactHatasiniCoz(hamMesaj)
    const mesaj = reactHatasi
      ? [reactHatasi.onek, reactHatasi.kayitMetni].filter(Boolean).join(' ')
      : hamMesaj

    if (!yakinZamandaGonderildiMi(`${tur}|${mesaj}`)) {
      gonderilenToplam += 1

      const yiginBolumleri = [
        girdi.yiginIzi?.trim(),
        girdi.bilesenYigini?.trim() ? `Bileşen yığını:${girdi.bilesenYigini}` : null,
        // Eşleşme yanlışsa kaydı okuyan kişi özgün metne dönebilmeli.
        reactHatasi ? `Özgün metin: ${hamMesaj}` : null,
        girdi.destekKodu ? `Destek kodu: ${girdi.destekKodu}` : null,
        ortamOzeti(),
      ].filter(Boolean)

      const govde = JSON.stringify({
        tur,
        mesaj: mesaj.slice(0, 2000),
        yiginIzi: yiginBolumleri.join('\n\n').slice(0, 20_000) || undefined,
        adres: window.location.pathname,
        surum: OPAS_DISPLAY_VERSION,
      })

      try {
        void fetch('/api/opas/hata-bildir', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: govde,
          credentials: 'include',
          keepalive: true,
        }).catch(() => {
          // Yutuluyor: hata bildirimi gönderilemediği için kullanıcıya ikinci bir hata gösterilmez.
        })
      } catch {
        // Yutuluyor.
      }
    }
  }

  // Bildirim tavanına takılsa bile kayma kurtarılır; keepalive yüzünden istek yenilemede düşmez.
  if (kayma) eskiPaketiBirKezYenile()
}

type BildirimEki = {
  /** Hatanın nereden geldiğini anlatan kısa önek, ör. «Uygulama çerçevesi». */
  baglam?: string
  bilesenYigini?: string
}

/** Bir istisnayı bildirime çevirir. Hata nesnesi olmayan değerler de (string, obje) gelebilir. */
export function hatayiBildir(hata: unknown, ek?: string | BildirimEki): void {
  const { baglam, bilesenYigini } = typeof ek === 'string' ? { baglam: ek, bilesenYigini: undefined } : (ek ?? {})

  if (hata instanceof Error) {
    hataBildir({
      tur: hata.name,
      mesaj: baglam ? `${baglam}: ${hata.message}` : hata.message,
      yiginIzi: hata.stack,
      bilesenYigini,
      // Next.js sunucu tarafındaki kaydı bu özetle işaretler; iki kaydı birleştiren tek ipucu bu.
      destekKodu: (hata as Error & { digest?: string }).digest,
    })
    return
  }

  const metin = typeof hata === 'string' ? hata : JSON.stringify(hata)
  hataBildir({ mesaj: baglam ? `${baglam}: ${metin}` : metin, bilesenYigini })
}
